import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { landingMarkup } from "./landingMarkup";
import { landingStyles } from "./landingStyles";
import PhysicsAnimation from "@/components/animations/PhysicsAnimation";
import Galaxy from "./Galaxy";

const setupLandingInteractions = (root: HTMLElement) => {
  const doc = root.ownerDocument;
  const scrollContainer = root.querySelector<HTMLElement>("#scroll-container");
  const progressFill = root.querySelector<HTMLElement>("#progress-fill");
  const dots = Array.from(root.querySelectorAll<HTMLButtonElement>(".nav-dot"));
  const tooltip = root.querySelector<HTMLElement>("#tooltip");
  const physicsM = root.querySelector<HTMLElement>("#physics-models");
  const astroM = root.querySelector<HTMLElement>("#astronomy-models");
  const astroPhysM = root.querySelector<HTMLElement>("#astrophysics-models");
  const quantumM = root.querySelector<HTMLElement>("#quantum-models");
  const sections = Array.from(root.querySelectorAll<HTMLElement>(".section"));
  const contents = Array.from(root.querySelectorAll<HTMLElement>(".section-content"));
  const models = Array.from(root.querySelectorAll<HTMLElement>(".model"));

  const createdStyles: HTMLStyleElement[] = [];
  let animationId: number | undefined;

  const addStyle = (css: string) => {
    const st = doc.createElement("style");
    st.textContent = css;
    doc.head.appendChild(st);
    createdStyles.push(st);
  };

  const handleScroll = () => {
    if (!scrollContainer) return;
    const st = scrollContainer.scrollTop;
    const sh = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    if (progressFill) {
      const pct = sh === 0 ? 0 : (st / sh) * 100;
      progressFill.style.height = `${pct}%`;
    }
    const idx = Math.round(st / (scrollContainer.clientHeight || 1));
    dots.forEach((d, i) => d.classList.toggle("active", i === idx));

    const particleLayers = [
      { id: "physics-particles", speed: -0.35 },
      { id: "astronomy-stars", speed: -0.25 },
      { id: "astrophysics-particles", speed: -0.3 },
      { id: "quantum-particles", speed: -0.35 },
    ];

    particleLayers.forEach((layer) => {
      const el = root.querySelector<HTMLElement>(`#${layer.id}`);
      if (!el) return;
      const section = el.closest<HTMLElement>(".section");
      if (!section) return;
      const off = st - section.offsetTop;
      el.style.transform = `translateY(${off * layer.speed}px)`;
    });

    sections.forEach((sec) => {
      const off = st - sec.offsetTop;
      const leftCol = sec.querySelector<HTMLElement>(".left-col");
      const modelsContainer = sec.querySelector<HTMLElement>(".models-container");
      if (leftCol) {
        leftCol.style.transform = `translateY(${off * -0.55}px)`;
        leftCol.style.willChange = "transform";
      }
      if (modelsContainer) {
        modelsContainer.style.transform = `translateY(${off * -0.2}px)`;
        modelsContainer.style.willChange = "transform";
      }
    });
  };

  const handleDotClick = (event: MouseEvent) => {
    const btn = event.currentTarget as HTMLButtonElement;
    const i = Number(btn.dataset.index || 0);
    if (!scrollContainer) return;
    scrollContainer.scrollTo({ top: i * scrollContainer.clientHeight, behavior: "smooth" });
  };

  let mouseX = 0;
  let mouseY = 0;
  let targetX = 0;
  let targetY = 0;

  const handleMouseMove = (e: MouseEvent) => {
    mouseX = (e.clientX / doc.defaultView!.innerWidth - 0.5) * 2;
    mouseY = (e.clientY / doc.defaultView!.innerHeight - 0.5) * 2;
  };

  const lerpLoop = () => {
    targetX += (mouseX - targetX) * 0.06;
    targetY += (mouseY - targetY) * 0.06;
    if (physicsM) physicsM.style.transform = `translate(${targetX * -18}px,${targetY * -12}px)`;
    if (astroM) astroM.style.transform = `translate(${targetX * 18}px,${targetY * 12}px)`;
    if (astroPhysM) astroPhysM.style.transform = `translate(${targetX * -14}px,${targetY * 14}px)`;
    if (quantumM) quantumM.style.transform = `translate(${targetX * 16}px,${targetY * -10}px)`;
    animationId = doc.defaultView?.requestAnimationFrame(lerpLoop);
  };

  const observer = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
    { threshold: 0.35 }
  );

  contents.forEach((el) => observer.observe(el));

  const createStars = (containerId: string, count: number) => {
    const el = root.querySelector<HTMLElement>(`#${containerId}`);
    if (!el) return;
    for (let i = 0; i < count; i += 1) {
      const s = doc.createElement("div");
      const sz = 1 + Math.random() * 2;
      s.style.cssText = `position:absolute;border-radius:50%;background:#fff;width:${sz}px;height:${sz}px;left:${
        Math.random() * 100
      }%;top:${Math.random() * 100}%;opacity:${0.15 + Math.random() * 0.75};animation:twinkle ${
        2 + Math.random() * 3
      }s ease-in-out infinite ${Math.random() * 3}s`;
      el.appendChild(s);
    }
  };

  const createQuantumParticles = (containerId: string, count: number, c1: string, c2: string) => {
    const el = root.querySelector<HTMLElement>(`#${containerId}`);
    if (!el) return;
    for (let i = 0; i < count; i += 1) {
      const p = doc.createElement("div");
      const sz = 3 + Math.random() * 3;
      const dur = 3 + Math.random() * 2;
      const dx = (Math.random() - 0.5) * 100;
      const dy = (Math.random() - 0.5) * 100;
      const dx2 = (Math.random() - 0.5) * 80;
      const dy2 = (Math.random() - 0.5) * 80;
      const nm = `qp${containerId}${i}`;
      addStyle(`@keyframes ${nm}{0%,100%{transform:translate(0,0) scale(1);opacity:0.8}25%{transform:translate(${dx}px,${dy}px) scale(0.8);opacity:0.4}75%{transform:translate(${dx2}px,${dy2}px) scale(1.1);opacity:0.6}}`);
      const shadow = c1.replace("rgb", "rgba").replace(")", ",0.5)");
      p.style.cssText = `position:absolute;border-radius:50%;width:${sz}px;height:${sz}px;left:${
        Math.random() * 100
      }%;top:${Math.random() * 100}%;background:linear-gradient(135deg,${c1},${c2});opacity:0.8;box-shadow:0 0 12px ${shadow};animation:${nm} ${
        dur
      }s ease-in-out infinite ${Math.random() * dur}s`;
      el.appendChild(p);
    }
  };

  const createAmbientParticles = (containerId: string, count: number, c1: string, c2: string) => {
    const el = root.querySelector<HTMLElement>(`#${containerId}`);
    if (!el) return;
    for (let i = 0; i < count; i += 1) {
      const p = doc.createElement("div");
      const sz = 3 + Math.random() * 2;
      const dur = 4 + Math.random() * 4;
      const dx = (Math.random() - 0.5) * 60;
      const dy = (Math.random() - 0.5) * 60;
      const nm = `ap${containerId}${i}`;
      addStyle(`@keyframes ${nm}{0%,100%{transform:translate(0,0);opacity:0.7}50%{transform:translate(${dx}px,${dy}px);opacity:0.3}}`);
      p.style.cssText = `position:absolute;border-radius:50%;width:${sz}px;height:${sz}px;left:${
        Math.random() * 100
      }%;top:${Math.random() * 100}%;background:linear-gradient(135deg,${c1},${c2});opacity:0.7;box-shadow:0 0 10px rgba(245,166,35,0.4);animation:${nm} ${
        dur
      }s ease-in-out infinite ${Math.random() * dur}s`;
      el.appendChild(p);
    }
  };

  // Extra details: supernova rays & rings
  const supernova = root.querySelector<HTMLElement>(".supernova");
  if (supernova) {
    for (let r = 0; r < 12; r += 1) {
      const ray = doc.createElement("div");
      ray.className = "sn-ray";
      ray.style.transform = `translate(-50%,-100%) rotate(${r * 30}deg)`;
      ray.style.animationDelay = `${r * 0.15}s`;
      supernova.appendChild(ray);
    }
    for (let rr = 0; rr < 3; rr += 1) {
      const ring = doc.createElement("div");
      ring.className = "sn-ring";
      ring.style.width = `${60 + rr * 20}px`;
      ring.style.height = `${60 + rr * 20}px`;
      ring.style.animationDelay = `${rr * 1}s`;
      supernova.appendChild(ring);
    }
  }

  // Double slit bands
  const doubleSlit = root.querySelector<HTMLElement>(".double-slit");
  if (doubleSlit) {
    for (let b = 0; b < 15; b += 1) {
      const band = doc.createElement("div");
      band.className = "interference-band";
      band.style.cssText = `width:40px;top:${5 + b * 6.5}%;animation-delay:${b * 0.12}s`;
      doubleSlit.appendChild(band);
    }
  }

  // Particles & stars
  createQuantumParticles("physics-particles", 100, "#06b6d4", "#10b981");
  createStars("astronomy-stars", 150);
  createAmbientParticles("astrophysics-particles", 60, "#F5A623", "#ea580c");
  createQuantumParticles("quantum-particles", 80, "#7BC843", "#4ade80");

  addStyle("@keyframes waveDrift{from{stroke-dashoffset:0}to{stroke-dashoffset:-100}}");

  if (scrollContainer) {
    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();
  }

  dots.forEach((d) => d.addEventListener("click", handleDotClick));
  doc.addEventListener("mousemove", handleMouseMove);
  lerpLoop();

  const modelCleanups: Array<() => void> = [];
  if (tooltip) {
    models.forEach((el) => {
      const enter = () => {
        tooltip.textContent = el.dataset.label || "";
        tooltip.style.display = "block";
      };
      const move = (e: MouseEvent) => {
        tooltip.style.left = `${e.clientX}px`;
        tooltip.style.top = `${e.clientY - 20}px`;
      };
      const leave = () => {
        tooltip.style.display = "none";
      };
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mousemove", move);
      el.addEventListener("mouseleave", leave);
      modelCleanups.push(() => {
        el.removeEventListener("mouseenter", enter);
        el.removeEventListener("mousemove", move);
        el.removeEventListener("mouseleave", leave);
      });
    });
  }

  return () => {
    if (scrollContainer) scrollContainer.removeEventListener("scroll", handleScroll);
    dots.forEach((d) => d.removeEventListener("click", handleDotClick));
    doc.removeEventListener("mousemove", handleMouseMove);
    if (animationId !== undefined && doc.defaultView) {
      doc.defaultView.cancelAnimationFrame(animationId);
    }
    observer.disconnect();
    modelCleanups.forEach((fn) => fn());
    createdStyles.forEach((st) => st.remove());
  };
};

const LandingContent = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return undefined;

    host.innerHTML = landingMarkup;

    // Mount Physics Three.js animation after HTML is rendered
    const animationContainer = host.querySelector("#physics-animation-container");
    if (animationContainer) {
      import("react-dom/client").then(({ createRoot }) => {
        const root = createRoot(animationContainer as HTMLElement);
        root.render(<PhysicsAnimation />);
      });
    }

    const authButtons = Array.from(host.querySelectorAll<HTMLButtonElement>("[data-auth]"));
    const scrollButtons = Array.from(host.querySelectorAll<HTMLButtonElement>("[data-scroll]"));

    const handleAuthClick = (event: Event) => {
      event.preventDefault();
      const target = event.currentTarget as HTMLElement;
      const intent = target.getAttribute("data-auth");
      if (intent === "login") {
        navigate("/signin");
      } else {
        navigate("/signup");
      }
    };

    const handleScrollClick = (event: Event) => {
      const target = event.currentTarget as HTMLElement;
      const id = target.getAttribute("data-scroll");
      if (!id) return;
      const section = host.querySelector(`#${id}`);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    };

    authButtons.forEach((btn) => btn.addEventListener("click", handleAuthClick));
    scrollButtons.forEach((btn) => btn.addEventListener("click", handleScrollClick));

    const cleanupInteractions = setupLandingInteractions(host);

    return () => {
      authButtons.forEach((btn) => btn.removeEventListener("click", handleAuthClick));
      scrollButtons.forEach((btn) => btn.removeEventListener("click", handleScrollClick));
      cleanupInteractions();
      host.innerHTML = "";
    };
  }, [navigate]);

  return (
    <div className="landing-shell">
      <style>{landingStyles}</style>
      <div className="landing-galaxy-layer">
        <Galaxy disableAnimation={Boolean(reduceMotion)} />
      </div>
      <div className="landing-content-host" ref={containerRef} />
    </div>
  );
};

export default LandingContent;
